// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { engineApi } from '@shared/data-access/api-base/citec-engine-api';
import {
  type PortfolioAllParams,
  type PortfolioUpdateParams,
  type PortfolioUpdateRequestBody,
  type MarketplaceFilterRequestBody,
  type PortfolioMarketPlaceResponseBody,
  type PortfolioCreateRequestBody,
  type PortfolioResponseBody,
} from '@shared/data-access/interfaces/api/engine/portfolio-types';

/**
 * Portfolio API - Citec Engine Integration.
 *
 * This API layer provides access to the **Citec Engine REST API** for managing user
 * portfolios and marketplace portfolio searches. The portfolio management system
 * allows users to create, update, and manage their investment portfolios with
 * support for holdings, benchmarks, and optimization status tracking.
 *
 * @remarks
 * Use this API to:
 *
 * - Retrieve all user portfolios with optional detail levels.
 * - Update existing portfolio information and holdings.
 * - Search marketplace portfolios using various filter criteria.
 *
 * Backed by RTK Query, this module abstracts RESTful operations into React hooks
 * for seamless integration with front-end applications. The API implements
 * intelligent caching and automatic invalidation for optimal performance.
 * @see {@link https://citecsolutions.com/apis} for full API reference.
 */
export const portfolioApi = engineApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Fetch all user portfolios with optional detail level.
     *
     * @remarks
     * This endpoint retrieves a list of all portfolios associated with the
     * authenticated user. The detail parameter controls whether basic or
     * comprehensive portfolio information is returned.
     * @example
     *
     * ```tsx
     * const {
     *   data: portfolios,
     *   isLoading,
     *   error,
     * } = useGetAllPortfoliosQuery({
     *   detail: 'true',
     * });
     * ```
     *
     * @param detail - Flag indicating whether to return detailed portfolio
     *   information.
     * @param signal - Optional AbortSignal for request cancellation.
     * @returns An array of portfolio data with complete portfolio structure.
     */
    getAllPortfolios: build.query<PortfolioResponseBody[], PortfolioAllParams>({
      query: ({ detail }) => ({
        url: '/citec/portfolio/all/',
        method: 'GET',
        params: { detail },
      }),
      providesTags: (result, error) => {
        if (error || !result) {
          return [];
        }

        return [
          ...result.map(({ portfolio_id }) => ({
            type: 'Portfolio' as const,
            id: portfolio_id,
          })),
          { type: 'Portfolio', id: 'All' },
        ];
      },
    }),

    /**
     * Fetch a specific portfolio by ID.
     *
     * @remarks
     * This endpoint retrieves detailed information about a specific portfolio
     * identified by its portfolio ID. The response includes all portfolio metadata,
     * holdings, and configuration details.
     * @example
     *
     * ```tsx
     * const {
     *   data: portfolio,
     *   isLoading,
     *   error,
     * } = useGetPortfolioQuery({
     *   portfolio_id: 123,
     * });
     * ```
     *
     * @param portfolio_id - Unique identifier of the portfolio to retrieve.
     * @returns Detailed portfolio information including all metadata and holdings.
     */
    getPortfolio: build.query<PortfolioResponseBody, PortfolioUpdateParams>({
      query: ({ portfolio_id }) => ({
        url: '/citec/portfolio/',
        method: 'GET',
        params: { portfolio_id },
      }),
      providesTags: (result, error, { portfolio_id }) => {
        if (error || !result) {
          return [];
        }
        return [{ type: 'Portfolio', id: portfolio_id }];
      },
    }),

    /**
     * Create a new portfolio.
     *
     * @remarks
     * This endpoint allows users to create a new portfolio with specified name,
     * benchmark, holdings, and other configuration details. The API validates the
     * portfolio data and returns the created portfolio.
     * @example
     *
     * ```tsx
     * const [createPortfolio, { isLoading }] = useCreatePortfolioMutation();
     *
     * const handleCreate = async () => {
     *   try {
     *     const newPortfolio = await createPortfolio({
     *       portfolio: 'My New Portfolio',
     *       benchmark: 'SPY',
     *       holdings: [],
     *       optimization_status: null,
     *       organizations: [],
     *       owner: 'user@example.com',
     *     }).unwrap();
     *     // Portfolio created successfully
     *   } catch (error) {
     *     // Handle error
     *   }
     * };
     * ```
     *
     * @param payload - Complete portfolio data structure for creation.
     * @returns The newly created portfolio object with assigned ID.
     */
    createPortfolio: build.mutation<
      PortfolioResponseBody,
      PortfolioCreateRequestBody
    >({
      query: payload => ({
        url: '/citec/portfolio/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Portfolio', id: 'All' }],
    }),

    /**
     * Update an existing portfolio with new data.
     *
     * @remarks
     * This endpoint allows users to modify portfolio information including
     * portfolio name, benchmark, holdings, and other metadata. The API
     * automatically invalidates cached portfolio data to ensure consistency.
     * @example
     *
     * ```tsx
     * const [updatePortfolio, { isLoading }] = useUpdatePortfolioMutation();
     *
     * const handleUpdate = async () => {
     *   try {
     *     await updatePortfolio({
     *       portfolio_id: 123,
     *       payload: updatedPortfolioData,
     *     }).unwrap();
     *     // Portfolio updated successfully
     *   } catch (error) {
     *     // Handle error
     *   }
     * };
     * ```
     *
     * @param portfolio_id - Unique identifier of the portfolio to update.
     * @param payload - Complete portfolio data structure for the update.
     * @returns The updated portfolio object with confirmation of the changes
     *   applied.
     */
    updatePortfolio: build.mutation<
      PortfolioResponseBody,
      PortfolioUpdateParams & { payload: PortfolioUpdateRequestBody }
    >({
      query: ({ portfolio_id, payload }) => ({
        url: '/citec/portfolio/',
        method: 'PUT',
        params: { portfolio_id },
        body: payload,
      }),
      invalidatesTags: (_, error, { portfolio_id }) => {
        if (error) {
          return [];
        }
        return [
          { type: 'Portfolio', id: 'All' },
          { type: 'Portfolio', id: portfolio_id },
        ];
      },
    }),

    /**
     * Delete a portfolio.
     *
     * @remarks
     * This endpoint allows users to delete a portfolio identified by its ID. The
     * deletion is permanent and will remove all associated holdings and
     * configuration data. The API automatically invalidates related cached data.
     * @example
     *
     * ```tsx
     * const [deletePortfolio, { isLoading }] = useDeletePortfolioMutation();
     *
     * const handleDelete = async () => {
     *   try {
     *     await deletePortfolio({ portfolio_id: 123 }).unwrap();
     *     // Portfolio deleted successfully
     *   } catch (error) {
     *     // Handle error
     *   }
     * };
     * ```
     *
     * @param portfolio_id - Unique identifier of the portfolio to delete.
     * @returns Confirmation of successful deletion.
     */
    deletePortfolio: build.mutation<void, PortfolioUpdateParams>({
      query: ({ portfolio_id }) => ({
        url: '/citec/portfolio/',
        method: 'DELETE',
        params: { portfolio_id },
      }),
      invalidatesTags: (_, error, { portfolio_id }) => {
        if (error) {
          return [];
        }
        return [
          { type: 'Portfolio', id: 'All' },
          { type: 'Portfolio', id: portfolio_id },
        ];
      },
    }),

    /**
     * Search marketplace portfolios using filter criteria.
     *
     * @remarks
     * This endpoint enables users to search for portfolios in the marketplace based
     * on various criteria such as ESG focus, risk level, client residency, and
     * asset manager. The search results provide comprehensive portfolio information
     * suitable for analysis and comparison.
     * @example
     *
     * ```tsx
     * const {
     *   data: searchResults,
     *   isLoading,
     *   error,
     * } = useSearchMarketplaceQuery({
     *   esg: 'Yes',
     *   risk_level: 'Conservative',
     *   client_residency: 'US Resident',
     * });
     * ```
     *
     * @param filters - Search criteria object containing optional filter values.
     * @returns An array of marketplace portfolios matching the specified filter
     *   criteria.
     */
    searchMarketplace: build.query<
      PortfolioMarketPlaceResponseBody[],
      MarketplaceFilterRequestBody
    >({
      query: filters => ({
        url: '/citec/marketplace/portfolio/search/',
        method: 'POST',
        body: filters,
      }),
      providesTags: (result, error) => {
        if (error || !result) {
          return [];
        }
        return [{ type: 'Portfolio', id: 'Marketplace' }];
      },
    }),
  }),
});
